"use client";

import { BookOpen, ChevronDown, Plus, Trash2, Upload } from "lucide-react";
import type { LevelDraft, OrganizationOption, PromptDraft } from "./types";
import { getJsonError } from "./types";

type CourseBasicsProps = {
    courseTitle: string;
    description: string;
    organizations: OrganizationOption[];
    selectedOrganizationId: string;
    onCourseTitleChange: (value: string) => void;
    onDescriptionChange: (value: string) => void;
    onOrganizationChange: (value: string) => void;
};

export function CourseBasicsSection(props: CourseBasicsProps) {
    const {
        courseTitle,
        description,
        organizations,
        selectedOrganizationId,
        onCourseTitleChange,
        onDescriptionChange,
        onOrganizationChange,
    } = props;

    return (
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <label htmlFor="course-title" className="text-sm font-semibold text-slate-700">Title</label>
            <input
                id="course-title"
                value={courseTitle}
                onChange={(e) => onCourseTitleChange(e.target.value)}
                placeholder="e.g. Data Analytics Foundations"
                className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm text-slate-900 shadow-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            />
            <label htmlFor="course-description" className="mt-4 block text-sm font-semibold text-slate-700">Description</label>
            <textarea
                id="course-description"
                value={description}
                onChange={(e) => onDescriptionChange(e.target.value)}
                rows={3}
                placeholder="Write a short course description"
                className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm text-slate-900 shadow-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            />

            <label className="mt-4 block text-sm font-semibold text-slate-700">Organization</label>
            <select
                value={selectedOrganizationId}
                onChange={(e) => onOrganizationChange(e.target.value)}
                className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm text-slate-900 shadow-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            >
                {organizations.length === 0 ? (
                    <option value="">No organizations found</option>
                ) : (
                    organizations.map((org) => (
                        <option key={org.id} value={String(org.id)}>{org.name}</option>
                    ))
                )}
            </select>
        </div>
    );
}

type MetadataProps = {
    domains: string[];
    futureGoals: string[];
    updateListItem: (list: "domains" | "futureGoals", index: number, value: string) => void;
    addListItem: (list: "domains" | "futureGoals") => void;
    removeListItem: (list: "domains" | "futureGoals", index: number) => void;
};

export function MetadataSection({
    domains,
    futureGoals,
    updateListItem,
    addListItem,
    removeListItem,
}: MetadataProps) {
    return (
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-sm font-semibold text-slate-700">Course Metadata</p>
            <div className="mt-3 space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Domains</p>
                {domains.map((domain, index) => (
                    <div key={`domain-${index}`} className="flex items-center gap-2">
                        <input
                            value={domain}
                            onChange={(e) => updateListItem("domains", index, e.target.value)}
                            placeholder="e.g. Data Science"
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                        />
                        {domains.length > 1 && (
                            <button type="button" onClick={() => removeListItem("domains", index)} className="rounded-md border border-red-200 bg-red-50 p-2 text-red-700 hover:bg-red-100">
                                <Trash2 className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                ))}
                <button type="button" onClick={() => addListItem("domains")} className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-700">
                    <Plus className="h-3.5 w-3.5" /> Add domain
                </button>

                <p className="pt-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Future goals</p>
                {futureGoals.map((goal, index) => (
                    <div key={`goal-${index}`} className="flex items-center gap-2">
                        <input
                            value={goal}
                            onChange={(e) => updateListItem("futureGoals", index, e.target.value)}
                            placeholder="e.g. ML Engineer"
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                        />
                        {futureGoals.length > 1 && (
                            <button type="button" onClick={() => removeListItem("futureGoals", index)} className="rounded-md border border-red-200 bg-red-50 p-2 text-red-700 hover:bg-red-100">
                                <Trash2 className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                ))}
                <button type="button" onClick={() => addListItem("futureGoals")} className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-700">
                    <Plus className="h-3.5 w-3.5" /> Add future goal
                </button>
            </div>
        </div>
    );
}

export function PromptsSection({
    prompts,
    onPromptChange,
}: {
    prompts: PromptDraft;
    onPromptChange: (field: keyof PromptDraft, value: string) => void;
}) {
    const fields: Array<{ key: keyof PromptDraft; placeholder: string }> = [
        { key: "generation_prompt", placeholder: "Canonical lesson prompt" },
        { key: "proactive_prompt", placeholder: "Proactive lesson prompt" },
        { key: "quiz_prompt", placeholder: "Quiz prompt" },
        { key: "placement_quiz_prompt", placeholder: "Placement quiz prompt" },
        { key: "remedial_lesson_prompt", placeholder: "Remedial lesson prompt" },
    ];

    return (
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-sm font-semibold text-slate-700">Prompts</p>
            <div className="mt-3 space-y-3">
                {fields.map((field) => (
                    <textarea
                        key={field.key}
                        value={prompts[field.key]}
                        onChange={(e) => onPromptChange(field.key, e.target.value)}
                        rows={3}
                        placeholder={field.placeholder}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                    />
                ))}
            </div>
        </div>
    );
}

type LevelsEditorProps = {
    levels: LevelDraft[];
    addLevel: () => void;
    updateLevelName: (levelId: string, value: string) => void;
    removeLevel: (levelId: string) => void;
    addModule: (levelId: string) => void;
    updateModuleTitle: (levelId: string, moduleId: string, value: string) => void;
    removeModule: (levelId: string, moduleId: string) => void;
    addLesson: (levelId: string, moduleId: string) => void;
    updateLessonField: (levelId: string, moduleId: string, lessonId: string, field: "title" | "content", value: string) => void;
    removeLesson: (levelId: string, moduleId: string, lessonId: string) => void;
};

export function LevelsEditor(props: LevelsEditorProps) {
    const {
        levels,
        addLevel,
        updateLevelName,
        removeLevel,
        addModule,
        updateModuleTitle,
        removeModule,
        addLesson,
        updateLessonField,
        removeLesson,
    } = props;

    return (
        <div className="space-y-4">
            {levels.map((level, levelIndex) => (
                <div key={level.id} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="mb-3 flex items-center justify-between">
                        <h2 className="text-sm font-bold text-slate-800">Level {levelIndex + 1}</h2>
                        <div className="flex items-center gap-2">
                            <button type="button" onClick={() => addModule(level.id)} className="inline-flex items-center gap-1 rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100">
                                <Plus className="h-3.5 w-3.5" /> Add module
                            </button>
                            <button type="button" onClick={() => removeLevel(level.id)} className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100">
                                <Trash2 className="h-3.5 w-3.5" /> Remove level
                            </button>
                        </div>
                    </div>

                    <input
                        value={level.name}
                        onChange={(e) => updateLevelName(level.id, e.target.value)}
                        placeholder="Level name"
                        className="mb-4 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm text-slate-900 shadow-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                    />

                    <div className="space-y-4">
                        {level.modules.map((module, moduleIndex) => (
                            <div key={module.id} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                                <div className="mb-3 flex items-center justify-between">
                                    <h3 className="text-sm font-bold text-slate-800">Module {moduleIndex + 1}</h3>
                                    <div className="flex items-center gap-2">
                                        <button type="button" onClick={() => addLesson(level.id, module.id)} className="inline-flex items-center gap-1 rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100">
                                            <Plus className="h-3.5 w-3.5" /> Add canonical lesson
                                        </button>
                                        <button type="button" onClick={() => removeModule(level.id, module.id)} className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100">
                                            <Trash2 className="h-3.5 w-3.5" /> Remove module
                                        </button>
                                    </div>
                                </div>

                                <input
                                    value={module.title}
                                    onChange={(e) => updateModuleTitle(level.id, module.id, e.target.value)}
                                    placeholder="Module title"
                                    className="mb-4 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm text-slate-900 shadow-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                                />

                                <div className="space-y-4">
                                    {module.lessons.map((lesson, lessonIndex) => {
                                        const jsonError = getJsonError(lesson.content);
                                        const hasJsonContent = lesson.content.trim().length > 0;
                                        const isValidJson = hasJsonContent && !jsonError;
                                        return (
                                            <div key={lesson.id} className="rounded-lg border border-slate-200 bg-white p-3">
                                                <div className="mb-2 flex items-center justify-between">
                                                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Canonical Lesson {lessonIndex + 1}</p>
                                                    <button type="button" onClick={() => removeLesson(level.id, module.id, lesson.id)} className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-[11px] font-semibold text-red-700 hover:bg-red-100">
                                                        <Trash2 className="h-3 w-3" /> Remove lesson
                                                    </button>
                                                </div>
                                                <input
                                                    value={lesson.title}
                                                    onChange={(e) => updateLessonField(level.id, module.id, lesson.id, "title", e.target.value)}
                                                    placeholder="Lesson title"
                                                    className="mb-2 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                                                />
                                                <textarea
                                                    value={lesson.content}
                                                    onChange={(e) => updateLessonField(level.id, module.id, lesson.id, "content", e.target.value)}
                                                    placeholder='{"sections":[{"title":"Intro","content":"..."}]}'
                                                    rows={5}
                                                    className={`w-full rounded-md border bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:ring-2 ${
                                                        jsonError ? "border-red-300 focus:border-red-500 focus:ring-red-100" : isValidJson ? "border-emerald-300 focus:border-emerald-500 focus:ring-emerald-100" : "border-slate-300 focus:border-indigo-500 focus:ring-indigo-100"
                                                    }`}
                                                />
                                                {jsonError ? (
                                                    <p className="mt-2 text-xs font-medium text-red-600">{jsonError}</p>
                                                ) : isValidJson ? (
                                                    <p className="mt-2 text-xs font-medium text-emerald-600">Valid JSON.</p>
                                                ) : (
                                                    <p className="mt-2 text-xs text-slate-500">Enter a valid JSON payload for this lesson.</p>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}

            <button type="button" onClick={addLevel} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                <Plus className="h-4 w-4" /> Add level
            </button>
        </div>
    );
}

type PreviewProps = {
    courseTitle: string;
    levels: LevelDraft[];
    totalModules: number;
    totalLessons: number;
    error: string | null;
    success: string | null;
    isSaveDisabled: boolean;
    isSaving: boolean;
    onSave: () => void;
};

export function PreviewPanel({
    courseTitle,
    levels,
    totalModules,
    totalLessons,
    error,
    success,
    isSaveDisabled,
    isSaving,
    onSave,
}: PreviewProps) {
    return (
        <aside className="lg:sticky lg:top-28 lg:h-fit">
            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="mb-4 border-b border-slate-100 pb-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Preview</p>
                    <h2 className="mt-1 text-lg font-bold text-slate-900">{courseTitle.trim() || "Untitled course"}</h2>
                    <p className="mt-1 text-xs text-slate-500">{levels.length} levels • {totalModules} modules • {totalLessons} lessons</p>
                </div>

                <div className="space-y-3">
                    {levels.map((level, levelIndex) => (
                        <div key={level.id} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                            <div className="mb-2 flex items-center justify-between px-1">
                                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{level.name.trim() || `Level ${levelIndex + 1}`}</p>
                                <span className="text-[10px] text-slate-400">{level.modules.length} modules</span>
                            </div>
                            <div className="space-y-3">
                                {level.modules.map((module, moduleIndex) => (
                                    <div key={module.id} className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                                        <div className="flex items-center justify-between p-4">
                                            <div className="flex items-center gap-3">
                                                <div className="rounded-lg bg-indigo-100 p-2">
                                                    <BookOpen className="h-4 w-4 text-indigo-600" />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-semibold text-gray-900">{module.title.trim() || `Module ${moduleIndex + 1}`}</p>
                                                    <p className="text-xs text-gray-500">{module.lessons.length} lessons</p>
                                                </div>
                                            </div>
                                            <ChevronDown className="h-4 w-4 text-gray-400" />
                                        </div>
                                        <div className="border-t border-gray-100 bg-gray-50/60 p-2">
                                            {module.lessons.length === 0 ? (
                                                <p className="px-2 py-1 text-xs text-gray-400">No lessons yet.</p>
                                            ) : (
                                                <div className="space-y-1">
                                                    {module.lessons.map((lesson, lessonIndex) => (
                                                        <div key={lesson.id} className="flex items-center justify-between rounded-md px-2 py-1.5">
                                                            <span className="text-xs font-medium text-gray-700">{lesson.title.trim() || `Lesson ${lessonIndex + 1}`}</span>
                                                            <span className="text-[10px] text-gray-400">{lesson.content.trim().length} chars</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                {error ? <p className="mt-3 rounded-md bg-red-50 px-3 py-2 text-xs font-medium text-red-700">{error}</p> : null}
                {success ? <p className="mt-3 rounded-md bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-700">{success}</p> : null}

                <button
                    type="button"
                    onClick={onSave}
                    disabled={isSaveDisabled || isSaving}
                    className={`mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold text-white ${
                        isSaveDisabled || isSaving ? "bg-slate-300 cursor-not-allowed" : "bg-indigo-600 hover:bg-indigo-700"
                    }`}
                >
                    <Upload className="h-4 w-4" />
                    {isSaving ? "Saving..." : "Save canonical lesson draft"}
                </button>
            </div>
        </aside>
    );
}
